import { useCallback, useEffect, useRef, useState } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { HeadingNode, $createHeadingNode, $isHeadingNode } from '@lexical/rich-text';
import {
  ListNode,
  ListItemNode,
  $isListNode,
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { LinkNode, TOGGLE_LINK_COMMAND, $isLinkNode } from '@lexical/link';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { $setBlocksType } from '@lexical/selection';
import {
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  $getSelection,
  $isRangeSelection,
  $getRoot,
  $createParagraphNode,
} from 'lexical';
import {
  sanitizeDescriptionHtml,
  looksLikeHtml,
  plainTextToHtml,
  isEmptyHtml,
} from '../../utils/sanitizeHtml';

const theme = {
  paragraph: 'mb-2 last:mb-0',
  heading: { h3: 'text-base font-bold text-slate-800 mb-2' },
  list: {
    ul: 'list-disc pl-6 mb-2',
    ol: 'list-decimal pl-6 mb-2',
    listitem: 'mb-0.5',
  },
  link: 'text-blue-600 underline',
  text: {
    bold: 'font-bold',
    italic: 'italic',
    underline: 'underline',
  },
};

const BTN_BASE =
  'h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors';
const BTN_ACTIVE = 'bg-slate-200 text-slate-900';

function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [active, setActive] = useState({});

  const refresh = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;
    const next = {
      bold: selection.hasFormat('bold'),
      italic: selection.hasFormat('italic'),
      underline: selection.hasFormat('underline'),
    };
    try {
      const anchor = selection.anchor.getNode();
      const top = anchor.getKey() === 'root' ? anchor : anchor.getTopLevelElementOrThrow();
      next.h3 = $isHeadingNode(top) && top.getTag() === 'h3';
      if ($isListNode(top)) {
        const t = top.getListType();
        next.ul = t === 'bullet';
        next.ol = t === 'number';
      }
      const fmt = top.getFormatType ? top.getFormatType() : '';
      next.align = fmt || 'left';
      const parent = anchor.getParent();
      next.link = $isLinkNode(parent) || $isLinkNode(anchor);
    } catch (e) { /* sélection vide */ }
    setActive(next);
  }, []);

  useEffect(() =>
    editor.registerUpdateListener(({ editorState }) => {
      editorState.read(refresh);
    }), [editor, refresh]);

  const toggleHeading = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () =>
          active.h3 ? $createParagraphNode() : $createHeadingNode('h3')
        );
      }
    });
  };

  const toggleList = (kind) => {
    if ((kind === 'ul' && active.ul) || (kind === 'ol' && active.ol)) {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(
        kind === 'ul' ? INSERT_UNORDERED_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND,
        undefined
      );
    }
  };

  const toggleLink = () => {
    if (active.link) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      return;
    }
    const url = window.prompt('URL du lien (https://…)');
    if (!url) return;
    const safe = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, { url: safe, target: '_blank', rel: 'noopener' });
  };

  const btn = (key, icon, title, onClick, isActive) => (
    <button
      type="button"
      data-testid={`rte-btn-${key}`}
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`${BTN_BASE} ${(isActive !== undefined ? isActive : active[key]) ? BTN_ACTIVE : ''}`}
    >
      <i className={`bi ${icon} text-[15px]`}></i>
    </button>
  );

  return (
    <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50/70 rounded-t-lg flex-wrap">
      {btn('bold', 'bi-type-bold', 'Gras', () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold'))}
      {btn('italic', 'bi-type-italic', 'Italique', () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic'))}
      {btn('underline', 'bi-type-underline', 'Souligné', () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline'))}
      <span className="w-px h-5 bg-slate-200 mx-1"></span>
      {btn('h3', 'bi-type-h3', 'Titre', toggleHeading)}
      <span className="w-px h-5 bg-slate-200 mx-1"></span>
      {btn('ul', 'bi-list-ul', 'Liste à puces', () => toggleList('ul'))}
      {btn('ol', 'bi-list-ol', 'Liste numérotée', () => toggleList('ol'))}
      <span className="w-px h-5 bg-slate-200 mx-1"></span>
      {btn('align-left', 'bi-text-left', 'Aligner à gauche', () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left'), (active.align || 'left') === 'left')}
      {btn('align-center', 'bi-text-center', 'Centrer', () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center'), active.align === 'center')}
      {btn('align-right', 'bi-text-right', 'Aligner à droite', () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right'), active.align === 'right')}
      {btn('align-justify', 'bi-justify', 'Justifier', () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify'), active.align === 'justify')}
      <span className="w-px h-5 bg-slate-200 mx-1"></span>
      {btn('link', 'bi-link-45deg', 'Lien', toggleLink)}
    </div>
  );
}

function ValueSyncPlugin({ value, lastHtmlRef }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    const v = value || '';
    if (v === (lastHtmlRef.current ?? '')) return;
    lastHtmlRef.current = v;
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      const html = looksLikeHtml(v) ? sanitizeDescriptionHtml(v) : plainTextToHtml(v);
      if (!html) {
        root.append($createParagraphNode());
        return;
      }
      const dom = new DOMParser().parseFromString(html, 'text/html');
      const nodes = $generateNodesFromDOM(editor, dom);
      let para = null;
      nodes.forEach((n) => {
        const t = n.getType ? n.getType() : '';
        if (['paragraph', 'heading', 'list'].includes(t)) {
          para = null;
          root.append(n);
        } else {
          if (!para) {
            para = $createParagraphNode();
            root.append(para);
          }
          para.append(n);
        }
      });
    });
  }, [value, editor, lastHtmlRef]);
  return null;
}

export const RichTextEditor = ({ value, onChange, placeholder, testId }) => {
  const lastHtmlRef = useRef(null);

  const initialConfig = {
    namespace: 'descriptif-rte',
    theme,
    onError: (e) => console.error('RichTextEditor:', e),
    nodes: [HeadingNode, ListNode, ListItemNode, LinkNode],
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div
        data-testid={testId || 'rich-text-editor'}
        className="rounded-lg border border-slate-200 bg-white focus-within:ring-2 focus-within:ring-blue-500/40 focus-within:border-blue-400 transition-shadow"
      >
        <ToolbarPlugin />
        <div className="relative">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                data-testid={testId ? `${testId}-content` : 'rte-content'}
                className="min-h-[170px] max-h-[420px] overflow-y-auto px-4 py-3 text-sm leading-relaxed text-slate-800 outline-none text-left [&_*]:text-left"
              />
            }
            placeholder={
              <div className="pointer-events-none absolute top-3 left-4 text-sm text-slate-400">
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
        <HistoryPlugin />
        <ListPlugin />
        <LinkPlugin />
        <ValueSyncPlugin value={value} lastHtmlRef={lastHtmlRef} />
        <OnChangePlugin
          ignoreSelectionChange
          onChange={(editorState, editor) => {
            editorState.read(() => {
              let html = sanitizeDescriptionHtml($generateHtmlFromNodes(editor, null));
              if (isEmptyHtml(html)) html = '';
              if (html !== (lastHtmlRef.current ?? '')) {
                lastHtmlRef.current = html;
                onChange(html);
              }
            });
          }}
        />
      </div>
    </LexicalComposer>
  );
};

export default RichTextEditor;
